import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useIsMobile } from "../lib/useIsMobile";

const STORAGE_KEY = "gvg-exit-intent-shown";
const SUBMITTED_KEY = "gvg-exit-intent-captured";
const MOBILE_INACTIVITY_MS = 25000;
const MOBILE_MIN_SCROLL = 0.4; // Must have scrolled at least 40% of the page

/**
 * ExitIntent — cinematic-zone abandonment capture.
 *
 * Desktop: fires when mouse leaves through the top of the viewport.
 * Mobile: fires after MOBILE_INACTIVITY_MS of no touch and past MOBILE_MIN_SCROLL depth.
 * Suppressed once per browser session and permanently after submission.
 *
 * Props:
 *   page: string    - route name used as analytics context (default "/diagnose")
 *   testid: string  - root element data-testid prefix
 */
export default function ExitIntent({ page = "/diagnose", testid = "exit-intent" }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const armed = useRef(false);

  const trigger = useCallback(() => {
    if (armed.current) return;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;
    if (window.localStorage.getItem(SUBMITTED_KEY)) return;
    armed.current = true;
    window.sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(true);
  }, []);

  // Desktop: mouse-out top of viewport
  useEffect(() => {
    if (isMobile) return undefined;
    let timer = null;
    const onLeave = (e) => {
      // Only when leaving through the top of the viewport
      if (e.clientY <= 0 && !e.relatedTarget) {
        // Small debounce so a quick flick to the tab bar doesn't misfire
        timer = window.setTimeout(trigger, 200);
      }
    };
    const onEnter = () => {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    document.addEventListener("mouseout", onLeave);
    document.addEventListener("mouseover", onEnter);
    return () => {
      document.removeEventListener("mouseout", onLeave);
      document.removeEventListener("mouseover", onEnter);
      if (timer) window.clearTimeout(timer);
    };
  }, [isMobile, trigger]);

  // Mobile: inactivity + scroll depth
  useEffect(() => {
    if (!isMobile) return undefined;
    let inactivityTimer = null;
    let hasScrolledEnough = false;

    const checkScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const ratio = window.scrollY / scrollable;
      if (ratio >= MOBILE_MIN_SCROLL) hasScrolledEnough = true;
    };

    const resetInactivity = () => {
      if (inactivityTimer) window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(() => {
        if (hasScrolledEnough) trigger();
      }, MOBILE_INACTIVITY_MS);
    };

    checkScroll();
    resetInactivity();

    const onInteract = () => {
      checkScroll();
      resetInactivity();
    };
    window.addEventListener("scroll", onInteract, { passive: true });
    window.addEventListener("touchstart", onInteract, { passive: true });
    return () => {
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("touchstart", onInteract);
      if (inactivityTimer) window.clearTimeout(inactivityTimer);
    };
  }, [isMobile, trigger]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("A working email, please.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/exit-intent/capture`, {
        email: clean,
        page,
        context: "diagnose-abandonment",
      });
      window.localStorage.setItem(SUBMITTED_KEY, "1");
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save your email. Try again.";
      toast.error(Array.isArray(detail) ? "Please check your email." : detail);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    // Reset local state so a re-open (rare) starts clean
    setTimeout(() => {
      setEmail("");
      setSubmitted(false);
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="exit-intent-scrim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-4 sm:px-6"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(15,15,15,0.85) 0%, rgba(0,0,0,0.94) 70%)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
          data-testid={`${testid}-scrim`}
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl rounded-t-3xl sm:rounded-3xl border border-brass/30 bg-[#0A0A0A] text-[#F5F1E8] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] overflow-hidden"
            data-testid={`${testid}-modal`}
          >
            {/* Champagne glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-[0.14] blur-3xl"
              style={{ background: "radial-gradient(circle, #C9A961 0%, transparent 70%)" }}
            />
            {/* Grain */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(201,169,97,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.35) 1px, transparent 1px)",
                backgroundSize: "56px 56px",
              }}
            />

            {/* Close */}
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              data-testid={`${testid}-close`}
              className="absolute top-4 right-4 h-9 w-9 rounded-full border border-brass/25 text-[#F5F1E8]/70 hover:text-[#C9A961] hover:border-brass/60 transition-colors flex items-center justify-center z-10"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative p-8 sm:p-10">
              {!submitted ? (
                <>
                  <p
                    className="font-sans text-[10px] uppercase tracking-[0.5em] text-[#C9A961] flex items-center gap-3"
                    data-testid={`${testid}-kicker`}
                  >
                    <span className="inline-block h-px w-6 bg-[#C9A961]/60" />
                    Before you go
                  </p>
                  <h2
                    className="mt-5"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: "clamp(1.6rem, 4.2vw, 2.6rem)",
                      lineHeight: 1.1,
                      letterSpacing: "-0.02em",
                      fontWeight: 500,
                      color: "#F5F1E8",
                    }}
                    data-testid={`${testid}-title`}
                  >
                    We&apos;ll send you your{" "}
                    <span className="italic text-[#C9A961]">diagnosis summary</span>.
                  </h2>
                  <p className="mt-4 font-sans text-sm sm:text-base text-[#F5F1E8]/75 leading-relaxed max-w-md">
                    Drop your email and we&apos;ll send a five-minute summary of the diagnosis, plus one link back so you can pick up where you left off.
                  </p>

                  <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row items-stretch gap-3" data-testid={`${testid}-form`}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      autoFocus
                      required
                      data-testid={`${testid}-email`}
                      className="flex-1 rounded-full bg-[#F5F1E8]/[0.04] border border-brass/25 px-5 py-3.5 font-sans text-base text-[#F5F1E8] placeholder:text-[#F5F1E8]/30 outline-none focus:border-[#C9A961] focus:bg-[#F5F1E8]/[0.06] transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid={`${testid}-submit`}
                      className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A961] px-6 py-3.5 font-sans text-[11px] uppercase tracking-mega font-semibold text-[#0A0A0A] hover:bg-[#D4B675] transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Email it to me"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </form>

                  <div className="mt-6 flex items-start gap-2.5 text-[#F5F1E8]/55">
                    <ShieldCheck className="h-4 w-4 text-[#C9A961]/80 shrink-0 mt-0.5" />
                    <p className="font-sans text-[11px] leading-relaxed">
                      One email. No newsletter drip. Unsubscribe from the footer of that single email.
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4" data-testid={`${testid}-thanks`}>
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="mx-auto h-14 w-14 rounded-full bg-[#C9A961] flex items-center justify-center shadow-[0_8px_30px_-6px_rgba(201,169,97,0.5)]"
                  >
                    <Check className="h-6 w-6 text-[#0A0A0A]" />
                  </motion.div>
                  <p className="mt-6 font-sans text-[10px] uppercase tracking-[0.5em] text-[#C9A961]">
                    On its way
                  </p>
                  <h3
                    className="mt-3"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: "clamp(1.6rem, 4vw, 2.3rem)",
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      fontWeight: 500,
                      color: "#F5F1E8",
                    }}
                  >
                    Thank you. Watch your inbox.
                  </h3>
                  <p className="mt-4 font-sans text-sm sm:text-base text-[#F5F1E8]/70 max-w-sm mx-auto">
                    Your diagnosis summary lands within a few minutes. Reply with anything you want us to look at first.
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    data-testid={`${testid}-continue`}
                    className="mt-8 rounded-full border border-brass/40 px-6 py-2.5 font-sans text-[11px] uppercase tracking-mega text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0A0A0A] transition-colors"
                  >
                    Keep reading
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
